import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowRight, 
  ArrowLeft, 
  Calendar, 
  Clock, 
  CheckCircle, 
  Globe, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  Sun,
  Moon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookCall = () => {
  const { currentTheme, toggleTheme, canToggle } = useTheme();
  
  const [step, setStep] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [userTimezone, setUserTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Settings from backend
  const [adminTimezone, setAdminTimezone] = useState('Asia/Dhaka');
  const [timeFormat, setTimeFormat] = useState('12');
  
  const [userDetails, setUserDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(tz);
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (step === 'calendar') {
      fetchAvailability();
    }
  }, [step]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings`);
      const data = await res.json();
      setAdminTimezone(data.admin_timezone || 'Asia/Dhaka');
      setTimeFormat(data.time_format || '12');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/questions`);
      const data = await res.json();
      setQuestions(data);
      setLoading(false);
      if (data.length === 0) {
        setStep('calendar');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
      setStep('calendar');
    }
  };

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/availability`);
      const data = await res.json();
      setAvailability(data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
    setLoading(false);
  };

  const convertTime = (time, date) => {
    try {
      const [hours, minutes] = time.split(':');
      
      // Create date string in admin timezone
      // We need to properly convert from admin timezone to user timezone
      const dateTimeStr = `${date}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
      
      // Create a date object and format it in admin timezone first, then convert to user timezone
      const options = { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: timeFormat === '12',
        timeZone: userTimezone
      };
      
      // Create the date assuming it's in admin timezone
      // Use Intl.DateTimeFormat to properly handle timezone conversion
      const adminDate = new Date(dateTimeStr);
      
      // Get the offset difference between admin timezone and UTC
      const adminFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: adminTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      // Parse the admin time components
      const adminParts = adminFormatter.formatToParts(new Date(`${date}T12:00:00Z`));
      const adminDateObj = new Date(`${date}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`);
      
      // Calculate the actual UTC time based on admin timezone
      const tempDate = new Date(dateTimeStr + getTimezoneOffset(adminTimezone, new Date(dateTimeStr)));
      
      // Format in user's timezone
      return tempDate.toLocaleTimeString('en-US', options);
    } catch (e) {
      console.error('Time conversion error:', e);
      // Fallback: just format the time as-is
      const [hours, minutes] = time.split(':');
      if (timeFormat === '12') {
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
      }
      return time;
    }
  };

  // Helper function to get timezone offset string
  const getTimezoneOffset = (tz, date) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    const offset = (utcDate - tzDate) / 60000; // in minutes
    const hours = Math.floor(Math.abs(offset) / 60);
    const mins = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    return `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Convert admin time to user's local time properly
  const convertAdminTimeToUserTime = (time, date) => {
    try {
      const [hours, minutes] = time.split(':');
      
      // Create a date in the admin's timezone
      const dateStr = `${date}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
      
      // Get UTC time by creating date in admin timezone
      const adminTime = new Date(dateStr);
      const adminOffset = getTimezoneOffsetMinutes(adminTimezone, adminTime);
      const utcTime = new Date(adminTime.getTime() - adminOffset * 60000);
      
      // Convert to user's timezone
      const userOffset = getTimezoneOffsetMinutes(userTimezone, utcTime);
      const userTime = new Date(utcTime.getTime() + userOffset * 60000);
      
      // Format the time
      const h = userTime.getHours();
      const m = userTime.getMinutes();
      
      if (timeFormat === '12') {
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    } catch (e) {
      console.error('Time conversion error:', e);
      return time;
    }
  };

  const getTimezoneOffsetMinutes = (tz, date) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return (tzDate - utcDate) / 60000;
  };

  // Get user-friendly timezone name
  const getUserTimezoneDisplay = () => {
    try {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const hours = Math.floor(Math.abs(offset) / 60);
      const mins = Math.abs(offset) % 60;
      const sign = offset >= 0 ? '+' : '-';
      const utcOffset = `UTC${sign}${hours}${mins > 0 ? ':' + String(mins).padStart(2, '0') : ''}`;
      
      // Get short timezone name if possible
      const tzName = userTimezone.split('/').pop().replace(/_/g, ' ');
      return `${tzName} (${utcOffset})`;
    } catch (e) {
      return userTimezone;
    }
  };

  const handleAnswer = (value) => {
    const question = questions[currentQuestionIndex];
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const nextQuestion = () => {
    const question = questions[currentQuestionIndex];
    if (question.required && !answers[question.id]) {
      return;
    }
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setStep('calendar');
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleDateSelect = (dateDoc) => {
    setSelectedDate(dateDoc);
    setSelectedSlot(null);
  };

  const handleSlotSelect = async (slot) => {
    if (slot.status === 'booked') return;
    setSelectedSlot(slot);
    try {
      await fetch(`${BACKEND_URL}/api/booking/lock-slot?date_id=${selectedDate.id}&slot_id=${slot.id}`, {
        method: 'POST'
      });
    } catch (e) {
      console.error('Error locking slot:', e);
    }
    setStep('details');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userDetails.name || !userDetails.email) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_id: selectedDate.id,
          slot_id: selectedSlot.id,
          user_name: userDetails.name,
          user_email: userDetails.email,
          user_phone: userDetails.phone,
          user_timezone: userTimezone,
          questionnaire_answers: answers
        })
      });
      const data = await res.json();
      if (data.success) {
        setBookingResult(data.booking);
        setStep('confirmation');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
    }
    setSubmitting(false);
  };

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const availableDate = availability.find(d => d.date === dateStr && d.slots.length > 0);
      days.push({
        day: i,
        dateStr,
        available: !!availableDate,
        dateDoc: availableDate
      });
    }
    return days;
  }, [currentMonth, availability]);

  const renderQuestion = (question) => {
    const value = answers[question.id] || '';
    
    switch (question.question_type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className={`w-full h-32 px-4 py-3 rounded-2xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none text-foreground backdrop-blur-sm ${
              currentTheme === 'dark'
                ? 'bg-white/5 border border-purple-500/20 placeholder:text-white/30'
                : 'bg-white/50 border border-black/10 placeholder:text-black/30'
            }`}
            placeholder="Type your answer..."
          />
        );
      case 'select':
      case 'radio':
        return (
          <div className="space-y-3">
            {question.options?.map((option, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleAnswer(option)}
                className={`w-full p-4 text-left rounded-2xl border backdrop-blur-sm transition-all font-medium ${
                  value === option
                    ? 'border-primary bg-primary/20 text-foreground'
                    : currentTheme === 'dark'
                      ? 'border-purple-500/20 bg-white/5 hover:border-primary/50 text-foreground'
                      : 'border-black/10 bg-white/50 hover:border-primary/50 text-foreground'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className={`w-full h-12 backdrop-blur-sm ${
              currentTheme === 'dark'
                ? 'bg-white/5 border-purple-500/20'
                : 'bg-white/50 border-black/10'
            }`}
            placeholder="Type your answer..."
          />
        );
    }
  };

  // Get step index for progress
  const getStepIndex = () => {
    switch(step) {
      case 'questions': return 0;
      case 'calendar': return 1;
      case 'slots': return 1;
      case 'details': return 2;
      case 'confirmation': return 3;
      default: return 0;
    }
  };

  const steps = questions.length > 0 
    ? ['Questions', 'Date', 'Details', 'Confirm']
    : ['Date', 'Time', 'Details', 'Confirm'];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        {/* Light theme background */}
        <div className={`absolute inset-0 transition-opacity duration-500 ${currentTheme === 'dark' ? 'opacity-0' : 'opacity-100'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-pink-100 via-purple-50 to-rose-100" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-200/50 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-200/50 rounded-full blur-3xl" />
        </div>
        
        {/* Dark theme cosmic background */}
        <div className={`absolute inset-0 transition-opacity duration-500 ${currentTheme === 'dark' ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950" />
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_rgba(236,72,153,0.2)_0%,_transparent_50%)]" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_left,_rgba(168,85,247,0.2)_0%,_transparent_50%)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,_rgba(236,72,153,0.15)_0%,_transparent_70%)]" />
          {/* Stars */}
          <div className="absolute inset-0 opacity-60" style={{
            backgroundImage: `radial-gradient(2px 2px at 20px 30px, white, transparent),
                             radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.8), transparent),
                             radial-gradient(1px 1px at 90px 40px, white, transparent),
                             radial-gradient(2px 2px at 160px 120px, rgba(255,255,255,0.9), transparent),
                             radial-gradient(1px 1px at 230px 80px, white, transparent),
                             radial-gradient(2px 2px at 300px 150px, rgba(255,255,255,0.7), transparent),
                             radial-gradient(1px 1px at 50px 160px, white, transparent),
                             radial-gradient(2px 2px at 120px 200px, rgba(255,255,255,0.8), transparent)`,
            backgroundSize: '350px 250px'
          }} />
          {/* Glow line */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-500/40 to-transparent" />
        </div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/10 dark:bg-black/10 border-b border-black/5 dark:border-white/5">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="text-sm sm:text-lg font-medium text-foreground flex items-center gap-1 sm:gap-2 shrink-0">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Site</span>
            <span className="sm:hidden">Back</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
              <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="max-w-[120px] sm:max-w-none truncate">{getUserTimezoneDisplay()}</span>
            </div>
            {canToggle && (
              <button
                onClick={toggleTheme}
                className="p-1.5 sm:p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                aria-label="Toggle theme"
              >
                {currentTheme === 'dark' ? (
                  <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 sm:pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          
          {/* Progress Steps */}
          <div className="flex justify-center mb-6 sm:mb-8 overflow-x-auto pb-2">
            <div className={`inline-flex items-center gap-0 px-3 sm:px-6 py-2 sm:py-3 rounded-full backdrop-blur-xl shadow-lg ${
              currentTheme === 'dark' 
                ? 'bg-slate-900/80 border border-purple-500/30' 
                : 'bg-white/70 border border-black/10'
            }`}>
              {steps.map((s, idx) => (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold transition-all ${
                      getStepIndex() >= idx 
                        ? 'bg-primary text-white' 
                        : currentTheme === 'dark' ? 'bg-white/10 text-white/50' : 'bg-black/10 text-muted-foreground'
                    }`}>
                      {getStepIndex() > idx ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> : idx + 1}
                    </div>
                    <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${
                      getStepIndex() >= idx ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {s}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-4 sm:w-8 h-0.5 mx-1.5 sm:mx-3 rounded-full shrink-0 ${
                      getStepIndex() > idx ? 'bg-primary' : currentTheme === 'dark' ? 'bg-white/10' : 'bg-black/10'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* Questions Step */}
            {step === 'questions' && questions.length > 0 && (
              <motion.div
                key="questions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-xl mx-auto"
              >
                <div className="relative">
                  <div className={`absolute -inset-2 rounded-3xl blur-2xl ${currentTheme === 'dark' ? 'bg-gradient-to-r from-pink-500/40 via-purple-500/40 to-pink-500/40' : 'bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-500/20'}`} />
                  <div className={`relative backdrop-blur-2xl rounded-3xl p-8 shadow-2xl ${
                    currentTheme === 'dark' 
                      ? 'bg-slate-900/95 border border-purple-500/30' 
                      : 'bg-white/95 border border-black/10'
                  }`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentTheme === 'dark' ? 'bg-primary/20' : 'bg-primary/10'}`}>
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm text-primary font-semibold">
                        Question {currentQuestionIndex + 1} of {questions.length}
                      </span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-foreground mb-6">
                      {questions[currentQuestionIndex].question}
                    </h2>

                    <div className="mb-8">
                      {renderQuestion(questions[currentQuestionIndex])}
                    </div>

                    <div className={`flex items-center justify-between pt-4 border-t ${currentTheme === 'dark' ? 'border-purple-500/20' : 'border-black/5'}`}>
                      <button
                        onClick={prevQuestion}
                        disabled={currentQuestionIndex === 0}
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-30"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </button>
                      <Button onClick={nextQuestion} className="bg-gradient-to-r from-primary to-pink-600 shadow-lg shadow-primary/30">
                        {currentQuestionIndex === questions.length - 1 ? 'Continue' : 'Next'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Calendar Step */}
            {step === 'calendar' && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="relative max-w-4xl mx-auto">
                  {/* Glow effect */}
                  <div className={`absolute -inset-2 rounded-3xl blur-2xl ${currentTheme === 'dark' ? 'bg-gradient-to-r from-pink-500/40 via-purple-500/40 to-pink-500/40' : 'bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-500/20'}`} />
                  
                  {/* Main Card */}
                  <div className={`relative backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden ${
                    currentTheme === 'dark' 
                      ? 'bg-slate-900/95 border border-purple-500/30 shadow-purple-500/20' 
                      : 'bg-white/95 border border-black/10'
                  }`}>
                    
                    <div className={`grid md:grid-cols-2 divide-x ${currentTheme === 'dark' ? 'divide-purple-500/20' : 'divide-black/5'}`}>
                      {/* Calendar Side */}
                      <div className="p-8">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentTheme === 'dark' ? 'bg-primary/20' : 'bg-primary/10'}`}>
                            <Calendar className="w-5 h-5 text-primary" />
                          </div>
                          <h2 className="text-xl font-bold text-foreground">Select a Date</h2>
                        </div>
                        <p className="text-muted-foreground text-sm mb-6 ml-13">Choose an available date for your call</p>

                        {/* Month Navigation */}
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-foreground">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </h3>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                              className={`p-2 rounded-xl transition-colors ${currentTheme === 'dark' ? 'bg-white/5 hover:bg-primary/20' : 'bg-black/5 hover:bg-primary/10'}`}
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                              className={`p-2 rounded-xl transition-colors ${currentTheme === 'dark' ? 'bg-white/5 hover:bg-primary/20' : 'bg-black/5 hover:bg-primary/10'}`}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Day Headers */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                            <div key={day} className={`text-center text-xs font-bold py-2 ${currentTheme === 'dark' ? 'text-purple-300/60' : 'text-muted-foreground/60'}`}>
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-1">
                          {calendarDays.map((day, idx) => (
                            <div key={idx} className="aspect-square p-0.5">
                              {day ? (
                                <button
                                  onClick={() => day.available && handleDateSelect(day.dateDoc)}
                                  disabled={!day.available}
                                  className={`w-full h-full rounded-xl flex items-center justify-center text-sm font-semibold transition-all ${
                                    selectedDate?.date === day.dateStr
                                      ? 'bg-gradient-to-br from-primary to-pink-600 text-white shadow-lg shadow-primary/40'
                                      : day.available
                                        ? currentTheme === 'dark'
                                          ? 'bg-purple-500/10 hover:bg-primary/30 text-white cursor-pointer border border-transparent hover:border-primary/30'
                                          : 'bg-primary/5 hover:bg-primary/20 text-foreground cursor-pointer border border-transparent hover:border-primary/30'
                                        : currentTheme === 'dark'
                                          ? 'text-white/15 cursor-not-allowed'
                                          : 'text-muted-foreground/20 cursor-not-allowed'
                                  }`}
                                >
                                  {day.day}
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>

                        {questions.length > 0 && (
                          <button
                            onClick={() => { setStep('questions'); setCurrentQuestionIndex(questions.length - 1); }}
                            className="flex items-center gap-2 text-muted-foreground hover:text-primary mt-6 text-sm transition-colors"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Questions
                          </button>
                        )}
                      </div>

                      {/* Time Slots Side */}
                      <div className={`p-8 ${currentTheme === 'dark' ? 'bg-purple-500/5' : 'bg-gradient-to-br from-black/[0.02] to-black/[0.05]'}`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentTheme === 'dark' ? 'bg-primary/20' : 'bg-primary/10'}`}>
                            <Clock className="w-5 h-5 text-primary" />
                          </div>
                          <h3 className="text-lg font-bold text-foreground">Available Times</h3>
                        </div>
                        
                        {selectedDate ? (
                          <div className="space-y-2">
                            {selectedDate.slots.map((slot) => {
                              const isBooked = slot.status === 'booked';
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => !isBooked && handleSlotSelect(slot)}
                                  disabled={isBooked}
                                  className={`w-full p-4 rounded-xl border flex items-center justify-between transition-all ${
                                    isBooked 
                                      ? 'bg-red-500/10 border-red-500/20 cursor-not-allowed'
                                      : currentTheme === 'dark'
                                        ? 'bg-white/5 border-white/10 hover:border-primary hover:bg-primary/10'
                                        : 'bg-white/50 border-black/5 hover:border-primary hover:bg-primary/5'
                                  }`}
                                >
                                  <span className={`font-semibold ${isBooked ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                                    {convertAdminTimeToUserTime(slot.start_time, selectedDate.date)}
                                  </span>
                                  {isBooked ? (
                                    <span className="text-xs text-red-500 font-bold px-2 py-1 bg-red-500/10 rounded-lg">BOOKED</span>
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-primary" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${currentTheme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}>
                              <Clock className="w-8 h-8 text-muted-foreground/30" />
                            </div>
                            <p className="text-muted-foreground">Select a date to see available times</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className={`px-8 py-4 border-t flex items-center justify-between text-sm text-muted-foreground ${currentTheme === 'dark' ? 'border-purple-500/10 bg-white/[0.02]' : 'border-black/5 bg-black/[0.02]'}`}>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span>Duration: <strong className="text-foreground">30 min</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary" />
                        <span>Your time: <strong className="text-foreground">{getUserTimezoneDisplay()}</strong></span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Details Step */}
            {step === 'details' && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-xl mx-auto"
              >
                <div className="relative">
                  <div className={`absolute -inset-2 rounded-3xl blur-2xl ${currentTheme === 'dark' ? 'bg-gradient-to-r from-pink-500/40 via-purple-500/40 to-pink-500/40' : 'bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-500/20'}`} />
                  <div className={`relative backdrop-blur-2xl rounded-3xl p-8 shadow-2xl ${
                    currentTheme === 'dark' 
                      ? 'bg-slate-900/95 border border-purple-500/30' 
                      : 'bg-white/95 border border-black/10'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentTheme === 'dark' ? 'bg-primary/20' : 'bg-primary/10'}`}>
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold text-foreground">Your Details</h2>
                    </div>
                    <p className="text-muted-foreground text-sm mb-6 ml-13">
                      {new Date(selectedDate.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {convertAdminTimeToUserTime(selectedSlot.start_time, selectedDate.date)}
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">Name *</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="text"
                            value={userDetails.name}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, name: e.target.value }))}
                            className={`pl-11 h-12 ${currentTheme === 'dark' ? 'bg-white/5 border-purple-500/20' : 'bg-white/50 border-black/10'}`}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">Email *</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="email"
                            value={userDetails.email}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                            className={`pl-11 h-12 ${currentTheme === 'dark' ? 'bg-white/5 border-purple-500/20' : 'bg-white/50 border-black/10'}`}
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">Phone (Optional)</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="tel"
                            value={userDetails.phone}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, phone: e.target.value }))}
                            className={`pl-11 h-12 ${currentTheme === 'dark' ? 'bg-white/5 border-purple-500/20' : 'bg-white/50 border-black/10'}`}
                            placeholder="+880 1234 567890"
                          />
                        </div>
                      </div>
                      <div className={`flex items-center justify-between pt-4 border-t ${currentTheme === 'dark' ? 'border-purple-500/20' : 'border-black/5'}`}>
                        <button
                          type="button"
                          onClick={() => {
                            fetch(`${BACKEND_URL}/api/booking/release-slot?date_id=${selectedDate.id}&slot_id=${selectedSlot.id}`, { method: 'POST' });
                            setSelectedSlot(null);
                            setStep('calendar');
                          }}
                          className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </button>
                        <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-primary to-pink-600 shadow-lg shadow-primary/30">
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Booking'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Confirmation Step */}
            {step === 'confirmation' && bookingResult && (
              <motion.div
                key="confirmation"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-xl mx-auto"
              >
                <div className="relative">
                  <div className={`absolute -inset-2 rounded-3xl blur-2xl ${currentTheme === 'dark' ? 'bg-gradient-to-r from-green-500/40 via-emerald-500/40 to-green-500/40' : 'bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20'}`} />
                  <div className={`relative backdrop-blur-2xl rounded-3xl p-8 shadow-2xl text-center ${
                    currentTheme === 'dark' 
                      ? 'bg-slate-900/95 border border-green-500/30' 
                      : 'bg-white/95 border border-black/10'
                  }`}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30"
                    >
                      <CheckCircle className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h2 className="text-2xl font-bold text-foreground mb-2">Booking Confirmed!</h2>
                    <p className="text-muted-foreground mb-6">A confirmation email has been sent to {bookingResult.user_email}</p>

                    <div className={`rounded-2xl p-4 mb-6 text-left space-y-3 ${currentTheme === 'dark' ? 'bg-white/5' : 'bg-black/5'}`}>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-primary" />
                        <span className="font-medium">
                          {new Date(bookingResult.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-primary" />
                        <span className="font-medium">{convertAdminTimeToUserTime(bookingResult.start_time, bookingResult.date)} ({getUserTimezoneDisplay()})</span>
                      </div>
                    </div>

                    <Link to="/">
                      <Button className="bg-gradient-to-r from-primary to-pink-600">
                        Back to Website
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default BookCall;
